// components/Receipt.tsx
import { m, AnimatePresence } from "framer-motion";
import { formatMoney, isQuoteOnly, type Estimate } from "@shared/commission";
import { useEstimate } from "../hooks/useEstimate";
import { useIntakeCopy } from "../i18n/useIntakeCopy";
import { CountUp } from "../motion/primitives";
import { DUR, EASE_ENTER } from "../motion/tokens";
import type { IntakeArtist } from "../types";

export function EstimateLines({ estimate }: { estimate: Estimate }) {
  const { c } = useIntakeCopy();
  return (
    <m.ul layout className="space-y-1.5 text-sm">
      <AnimatePresence initial={false}>
        {estimate.lines.map((l) => (
          <m.li key={l.key} layout initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0, transition: { duration: DUR.ui, ease: EASE_ENTER } }} exit={{ opacity: 0, x: -8 }}
            className="flex justify-between gap-3">
            <span className={l.kind === "ask" ? "text-[var(--color-text-muted)]" : "text-[var(--color-text)]"}>{l.label}</span>
            <span className={l.kind === "ask" ? "italic text-[var(--color-text-muted)]" : "tabular-nums text-[var(--color-text)]"}>
              {l.amount === null ? c.receipt.ask : `${l.from ? `${c.receipt.from} ` : ""}${formatMoney(l.amount)}`}
            </span>
          </m.li>
        ))}
      </AnimatePresence>
    </m.ul>
  );
}

export function Receipt({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const estimate = useEstimate(artist);
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
      <h3 className="mb-3 font-(family-name:--font-display) font-normal text-xl text-[var(--color-text)]">{c.receipt.title}</h3>
      {estimate.lines.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{c.receipt.empty}</p>
      ) : (
        <>
          <EstimateLines estimate={estimate} />
          <div className="mt-4 flex items-baseline justify-between border-t border-[var(--color-border)] pt-3">
            <span className="text-sm text-[var(--color-text-muted)]">{c.receipt.total}</span>
            <span className="text-2xl font-semibold text-[var(--color-primary)]">
              {(estimate.hasFrom || estimate.hasAsk) && !isQuoteOnly(estimate) && <span className="mr-1 text-sm font-normal text-[var(--color-text-muted)]">{c.receipt.from}</span>}
              {isQuoteOnly(estimate)
                ? <span className="text-base font-normal italic text-[var(--color-text-muted)]">{c.receipt.ask}</span>
                : <CountUp value={estimate.total} format={formatMoney} />}
            </span>
          </div>
        </>
      )}
      <p className="mt-3 text-xs text-[var(--color-text-muted)]">{fmt(c.receipt.note, { artist: artist.name })}</p>
    </div>
  );
}
